import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { TextLink } from '@lastbrand/web-ui/text-link'
import { css } from '@lastbrand/web-ui-styled-system/css'
import React from 'react'

import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentWrapper } from '@src/ds/blocks/section-content-wrapper'
import { Image } from '@src/quince-components/src/atoms/image'
import { Link } from '@src/quince-components/src/atoms/link'

import type { FeatureCardsDSProps, FeatureCardsRow } from './feature-cards-ds.types'
import type { Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * DS port of the PL FEATURE_CARDS widget: borderless feature cards on an optional CMS band.
 * Desktop N-column grid; mobile horizontal scroll rail. Telemetry-free.
 */

// SCC gutters. At `base` the END pad drops to 0 so the rail can bleed to that screen edge; the START pad
// STAYS, because the heading below shares this band and needs its gutter — the rail claws that side back
// itself (see `gridClass`).
//
// Written as two LONGHANDS rather than `paddingInline` plus an override: two rules for the same side
// would be equal-specificity classes resolved by Panda's emit order, which is the silent-shadowing trap
// this codebase has been bitten by. Spelling both sides out at both breakpoints leaves nothing to
// resolve. Logical, not physical, so the bleed and the gutter it cancels stay on the same side in RTL.
//
// `base`/`lg` rather than the legacy `@media (max-width: 960px)` this used: 960 came from the SCSS
// `mobileOnly` breakpoint, but the rail is a rail everywhere below Panda's `lg` (1200px), so gating the
// bleed at 960 left 961–1199px scrolling with no bleed on either edge.
//
// Block padding is the Figma `Content` block padding: `py-48` mobile (6998:40873), `py-100` desktop
// (6998:40863) — `space-100` is 6.25rem = 100px.
//
// Width, box-sizing and the `bp-desktop-cap` background cap come from `SectionContentWrapper`.
const bandClass = css({
  paddingBlock: { base: 'space-48', lg: 'space-100' },
  paddingInlineStart: { base: 'space-16', lg: 'space-48' },
  paddingInlineEnd: { base: '0', lg: 'space-48' },
})

const contentFrameClass = css({
  width: 'full',
  maxWidth: '1504px',
  marginInline: 'auto',
})

// Figma `Content` gap title→list: 24px mobile, 48px desktop. Type is `fontsize/24` leading 1.18 →
// `title-24-dec` (24/28, matching the frame's 28px title height) stepping to `fontsize/32` leading 1.24 →
// `title-32-dec` (32/40, matching its 40px height).
const headingClass = css({
  margin: '0',
  marginBottom: { base: 'space-24', lg: 'space-48' },
  color: 'black',
  textStyle: { base: 'title-24-dec', lg: 'title-32-dec' },
})

// Mirrors `row-of-image-text-blocks-ds.tsx` (the non-PL twin of this section): mobile is the
// `blocksRailClass` horizontal scroll rail at space-16 — NOT a stacked column — and desktop the
// `blocksGridClass` equal N-column grid at space-40 (Figma `Widget-FeatureCards` 6998:40839 desktop
// `List` 6998:40867: `itemSpacing: 40`, 4 `Card` children at 346 wide — `4×346 + 3×40 = 1504`, exactly
// the 1504px content frame). The rail also matches this widget's own legacy path, whose `.grid` goes
// `flex-flow: row nowrap; overflow-x: auto` on mobile. `alignItems: start` stops the grid's default
// `stretch` from inflating the aspect-boxed Image atom to the tallest row.
const gridClass = css({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  gap: 'space-16',
  alignItems: 'start',
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': { display: 'none' },
  // Full-bleed rail, both screen edges. The END edge is already there: `bandClass` drops its end pad at
  // this same width. The START edge needs claiming, and a negative start margin is all it takes — the
  // rail is a block-level flex container with `width: auto`, so the used width absorbs the -16px and the
  // start edge moves out while the end edge stays exactly where the band's missing pad left it. (A
  // `width: calc(100% + …)` override, needed where the box has a definite width, would double-count here.)
  //
  // The gutters then ride on the EDGE CARDS so the resting row still lines up with the heading above.
  // Margins, not padding: the tiles are a fixed `15rem` under `box-sizing: border-box`, so padding would
  // shrink a card by 16px instead of adding room beside it. No scroll-snap on this rail, so the leading
  // inset needs no `scroll-padding` companion — nothing can snap it away.
  //
  // All three share the SAME `base`/`lg` gating as the band's dropped end pad, deliberately: an inset
  // whose bleed stops at a different width leaves a range with the inset and no bleed — the first card
  // 32px from the edge instead of 16px, the bug this pattern keeps producing. They reset at `lg`, where
  // this element stops being a rail and becomes the grid below.
  marginInlineStart: { base: 'calc(var(--web-ui-spacing-space-16) * -1)', lg: '0' },
  '& > *:first-child': { marginInlineStart: { base: 'space-16', lg: '0' } },
  '& > *:last-child': { marginInlineEnd: { base: 'space-16', lg: '0' } },
  lg: {
    display: 'grid',
    gridTemplateColumns: 'repeat(var(--feature-cards-columns, 3), 1fr)',
    gap: 'space-40',
    overflowX: 'visible',
  },
})

// Rail item on mobile — 66vw per Figma `Widget-FeatureCards` (node 6998:40839): "On Mobile →
// Component becomes a free slider, where Cards are 66% of viewport width", annotated "257px (66% vpw
// of 390px)" / "343px (66% vpw of 520px)". Matches the non-PL `cardRailItemClass` — NOT the legacy
// fixed 240px, which left the next card's peek dependent on viewport width. The desktop grid controls
// the width, so the cap is released at `lg`. (The 390 `Card` frame reads `w-[257px]`, but that is the
// resolved 66vw at that one width — a fixed 257 would be 86px short at the annotated 520 case.)
//
// The card is also the Figma `Card` frame's box: `flex flex-col gap-16 items-start` — a single uniform
// 16px rhythm between image, header and CTA, which is why the children below carry no margins of their own.
const cardClass = css({
  color: 'black',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'space-16',
  flexShrink: 0,
  width: '66vw',
  lg: { flexShrink: 1, width: 'auto' },
})

// `aspect-[358/358]` (1:1) at `w-full` in both frames, so the image is always as wide as the card: 346² on
// the 1504 desktop frame, and the 66vw rail width on mobile (257² at 390, 343² at 520). The square comes
// from the `Image` atom's own default box — it derives `width / height` from those props and falls back to
// `1` when they are absent, which is why none are passed (see `FeatureCardMedia`).
const mediaClass = css({
  display: 'block',
  width: '100%',
  height: 'auto',
})

// Figma icon frame (7007:50691) is 40px but its glyph is 32×32 — size to the glyph. Mobile has no Figma node.
const iconClass = css({
  display: 'block',
  width: { base: '1.75rem', lg: '2rem' },
  height: { base: '1.75rem', lg: '2rem' },
  objectFit: 'contain',
  flexShrink: 0,
})

// The Figma `Header`: `flex flex-col gap-8 w-full`. The icon lives INSIDE this box (7007:50304 renders
// ReplaceIcon → title → description), so it sits 8px above the title rather than 16px as a card-level sibling.
const cardHeaderClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'space-8',
  width: '100%',
})

// Card title is `title/14` in BOTH frames — Grosa Medium 14px/20px, `letter-spacing: 0.05em` (0.7px at
// 14px, matching the frame's `tracking-[0.7px]`) and uppercase, all carried by the DS text style.
const titleClass = css({
  margin: '0',
  color: 'black',
  textStyle: 'title-14',
})

const buttonsClass = css({ display: 'flex', flexWrap: 'wrap', gap: 'space-8' })

// No-href CTA: same `TextLink` recipe, no interactive semantics. `TextLink` resolves its element as
// `LinkComponent ?? 'button'` and only injects `type: 'button'` on the bare-button path, so handing it
// a span shim keeps the DS visual while dropping the tab stop, the button role and the `type` — this
// widget is telemetry-free, so an unlinked CTA has nothing to activate. `cursor` is reset because the
// recipe's base sets `pointer`.
const ctaTextCss = css.raw({ cursor: 'default' })

// Module scope, not inline in the CTA: a component defined during render is a new type each pass and
// would remount the anchor on every update.
const CtaLink = (linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <Link {...linkProps} withoutBaseStyle hidePointerCursor />
)

const CtaText = (textProps: React.HTMLAttributes<HTMLSpanElement>) => <span {...textProps} />

const BODY_TYPO_BASE = { textStyle: 'body-14', color: 'black' } as const

function FeatureCardMedia({ media, className }: Readonly<{ media?: Media; className: string }>) {
  const content = media?.content
  if (!content?.url) return null

  // Proto media URLs are protocol-relative — prepend the scheme so the CDN transform resolves.
  const url = content.url.startsWith('//') ? `https:${content.url}` : content.url

  if (media?.type === MediaType.VIDEO) {
    return (
      <video className={className} src={url} muted playsInline loop autoPlay aria-label={content.alt ?? undefined} />
    )
  }

  // SVGs bypass the raster CDN pipeline — render as a plain <img>.
  if (/\.svg(\?|#|$)/i.test(url)) {
    return (
      <img
        className={className}
        src={url}
        alt={content.alt ?? ''}
        loading="lazy"
        width={content.width ? content.width : undefined}
        height={content.height ? content.height : undefined}
      />
    )
  }

  // Deliberately NO `width`/`height`: the Image atom derives its aspect box from them
  // (`props.width && props.height ? w / h : 1`), so passing the asset's intrinsic size gave each card
  // a different height — a 1600×2000 photo boxed at 125% next to a 1600×1600 one at 100%. Omitting
  // them falls back to the square box the non-PL twin uses (it passes no size either) and matches the
  // Figma card image (346² desktop / 257² mobile).
  return <Image url={url} title={content.alt ?? ''} alt={content.alt ?? ''} className={className} loading="lazy" />
}

function FeatureCard({ row, index }: Readonly<{ row: FeatureCardsRow; index: number }>) {
  return (
    <article className={cardClass}>
      <FeatureCardMedia media={row.media} className={mediaClass} />
      {/* Icon + title + description share the Figma `Header` box: 8px apart, inside the card's 16px rhythm. */}
      {row.icon || row.title || row.subTitle ? (
        <div className={cardHeaderClass}>
          <FeatureCardMedia media={row.icon} className={iconClass} />
          {row.title ? (
            <h3 className={titleClass} data-testid={TEST_LOCATORS.FEATURE_CARDS.CARD_TITLE(index)}>
              {row.title}
            </h3>
          ) : null}
          {row.subTitle ? <RichTextDS content={row.subTitle} baseStyle={BODY_TYPO_BASE} /> : null}
        </div>
      ) : null}
      {row.buttons.length ? (
        <div className={buttonsClass}>
          {row.buttons.map((button, index) =>
            button.href ? (
              <TextLink key={`${button.label}-${index}`} caps size="lg" LinkComponent={CtaLink} href={button.href}>
                {button.label}
              </TextLink>
            ) : (
              <TextLink
                key={`${button.label}-${index}`}
                caps
                size="lg"
                css={ctaTextCss}
                LinkComponent={CtaText as never}
              >
                {button.label}
              </TextLink>
            ),
          )}
        </div>
      ) : null}
    </article>
  )
}

export function FeatureCardsDS({ heading, rows, backgroundColor, testId }: FeatureCardsDSProps) {
  if (rows.length === 0) return null

  return (
    <SectionContentWrapper
      as="section"
      data-testid={testId}
      className={bandClass}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className={contentFrameClass}>
        {heading ? (
          <h2 className={headingClass} data-testid={TEST_LOCATORS.FEATURE_CARDS.HEADING}>
            {heading}
          </h2>
        ) : null}
        <div className={gridClass} style={{ '--feature-cards-columns': rows.length } as React.CSSProperties}>
          {rows.map((row, index) => (
            <FeatureCard key={row.key} row={row} index={index} />
          ))}
        </div>
      </div>
    </SectionContentWrapper>
  )
}
